import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { catchError, EMPTY, Observable, switchMap, throwError } from 'rxjs';
import { SILENT_REQUEST } from '../../http/silent-request.context';
import { SUPPRESS_ERROR_TOAST } from '../../http/suppress-error-toast.context';
import { AuthErrorCode } from '../../enums/auth-error-code.enum';
import { CommonRoute } from '../../enums/common-route.enum';
import { HttpCustomHeader } from '../../enums/http-custom-header.enum';
import { ToastLife } from '../../enums/toast-life.enum';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { LogFactory } from '../../services/logger/log-factory';
import { ReturnUrlService } from '../../services/return-url/return-url.service';
import { StringService } from '../../utils/string/string.service';

// A 401 from these MFA credential-check endpoints means "wrong code", NOT an
// expired session. Treating them as session expiry showed a misleading toast
// and bounced the user to /login mid-MFA. Surface the API's message instead.
const MFA_CODE_CHECK_PATHS = [
  '/authentication/mfa/verify',
  '/authentication/mfa/disable',
  '/authentication/mfa/confirm-registration',
];

// Auth endpoints (login/refresh/logout/mfa/…). A 401 on one of these must never
// trigger the silent-renew replay below, or a refresh/login failure would
// recurse. Matches the TokenMiddleware's own `/authentication` bypass.
const AUTH_URL_SEGMENT = '/authentication/';

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptorService implements HttpInterceptor {
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly returnUrlService = inject(ReturnUrlService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly stringService = inject(StringService);

  private readonly logger = inject(LogFactory).createLogger(
    HttpErrorInterceptorService,
  );

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const isLoginRequest = req.url.includes(CommonRoute.LOGIN);
        const isLogoutRequest = req.url.includes(CommonRoute.LOGOUT);
        const isMfaCodeCheck = MFA_CODE_CHECK_PATHS.some((p) =>
          req.url.includes(p),
        );
        let isHandled = false;

        // Silent renew (ADR-0002/0122): a TOKEN_OUTDATED 401 means the admin's
        // token is valid but stale (permissions changed / version bumped / just
        // expired). Re-mint once via the session cookie (ADR-0122) and replay the
        // ORIGINAL request with the new access token — no logout, no toast. Only
        // a failed refresh logs out (handled inside refreshSession). Skip the
        // auth endpoints themselves so a refresh/login 401 can't recurse here.
        const errorCode = (
          error.error as { error?: { errorCode?: string } } | null
        )?.error?.errorCode;
        const isAuthFlowUrl = req.url.includes(AUTH_URL_SEGMENT);
        if (
          error.status === 401 &&
          errorCode === AuthErrorCode.TOKEN_OUTDATED &&
          !isAuthFlowUrl
        ) {
          this.logger.debug(
            'TOKEN_OUTDATED received — renewing admin session and replaying request.',
          );
          return this.authenticationService.refreshSession().pipe(
            switchMap((newAccessToken) =>
              next.handle(this.withToken(req, newAccessToken)),
            ),
            catchError((refreshError) => {
              this.logger.warn(
                'Session refresh failed after TOKEN_OUTDATED; surfacing original error.',
                refreshError,
              );
              return throwError(() => error);
            }),
          );
        }

        // The caller's catch logs the failure at error with its business context;
        // this line only records the request, for a developer.
        this.logger.debug('HTTP request failed', {
          status: error.status,
          method: req.method,
          url: req.url,
        });

        if (error.status === 401 && !isLoginRequest && !isMfaCodeCheck) {
          this.logger.warn('Unauthorized (401) Error Handled.');
          this.messageService.add({
            severity: 'warn',
            summary: 'Session Expired',
            detail: 'Your session has expired. Please log in again.',
            life: ToastLife.MEDIUM,
          });

          if (this.router.url !== CommonRoute.LOGIN) {
            this.router.navigate([CommonRoute.LOGIN], {
              queryParams: {
                returnUrl: this.returnUrlService.getSanitizedReturnUrl(
                  this.router.url,
                ),
              },
            });
          }

          isHandled = true;
        } else if (!req.context.get(SILENT_REQUEST) && !req.context.get(SUPPRESS_ERROR_TOAST)) {
          // SUPPRESS_ERROR_TOAST: the calling screen shows its own message for
          // a 4xx (e.g. the reset page's "link expired"), so no generic toast;
          // the error is rethrown below to reach it. 401 handling above still runs.
          // A silent request (background poll, an inline-reported probe) skips
          // the toast and is rethrown below so the caller handles it itself.
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.describeError(error),
            life: ToastLife.MEDIUM,
          });
          isHandled = true;
        }
        // Special handling for logout failure
        if (isLogoutRequest) {
          this.logger.error('Logout Request Failed:', error);
          this.authenticationService.cleanSession().then(() => {
            this.router.navigate([CommonRoute.LOGIN]);
          });
        }

        if (isHandled) {
          return EMPTY;
        } else {
          return throwError(() => error);
        }
      }),
    );
  }

  /** Clone the request with the freshly-minted access token in the auth header. */
  private withToken(
    req: HttpRequest<unknown>,
    accessToken: string,
  ): HttpRequest<unknown> {
    const bearer =
      this.stringService.getBearerToken(accessToken) ?? accessToken;
    return req.clone({
      headers: req.headers.set(HttpCustomHeader.ACCESS_TOKEN_HEADER, bearer),
    });
  }

  /**
   * Build a user-facing message: generic "Server error" for unreachable backend
   * (status 0) and server faults (5xx); the API's own message for client errors;
   * never the HTTP status code.
   *
   * NOTE (security review, ADR-0099): surfacing the backend's raw 4xx `message`
   * can leak internal detail (bucket/entity names). We deliberately keep it here
   * because admin is authenticated-staff-only (LOW risk) AND many admin flows —
   * MFA verify, form validation ("Email already exists") — rely on this toast to
   * show the actionable backend message; blanket-genericising it would regress
   * those. The proper fix is backend-side message hygiene (don't emit internal
   * detail in user-facing messages), tracked separately.
   */
  private describeError(error: HttpErrorResponse): string {
    if (error.status === 0) {
      return 'Server error. Please check your connection and try again.';
    }
    if (error.status >= 500) {
      return 'Server error. Please try again later.';
    }
    const message = (error.error as { message?: unknown })?.message;
    if (Array.isArray(message) && message.length) return String(message[0]);
    if (typeof message === 'string' && message) return message;
    return 'Something went wrong. Please try again.';
  }
}

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const interceptor = inject(HttpErrorInterceptorService);
  const nextHandler: HttpHandler = {
    handle: (request: HttpRequest<any>) => next(request),
  };
  return interceptor.intercept(req, nextHandler);
};
